import type { CollectionPreferencesProps } from '@risk-smart/themed-cloudscape-components/collection-preferences';
import CollectionPreferences from '@risk-smart/themed-cloudscape-components/collection-preferences';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { FieldDefinitionWithDataSourceIndex } from './customDatasourceModel';
import { PAGE_SIZE_OPTIONS } from './tablePreferences';

type Props = Pick<CollectionPreferencesProps, 'onConfirm' | 'preferences'> & {
  fields: FieldDefinitionWithDataSourceIndex[];
  columnsAlwaysVisible: boolean;
};

const CustomDatasourceCollectionPreferences: FC<Props> = ({
  fields,
  columnsAlwaysVisible,
  ...rest
}) => {
  const { t } = useTranslation(['common']);

  const collectionPreferencesProps: CollectionPreferencesProps = {
    ...rest,
    title: t('tables.preferences'),
    confirmLabel: t('tables.confirm'),
    cancelLabel: t('tables.cancel'),
    pageSizePreference: {
      title: t('tables.page_size'),
      options: PAGE_SIZE_OPTIONS.map((size) => ({
        value: size,
        // Custom data sources are entity-agnostic, so the generic "N items" label is
        // used rather than the register-name variant the entity tables use.
        label: t('tables.paging_option_items', { size }),
      })),
    },
    contentDisplayPreference: {
      title: t('tables.preferences_columns_title'),
      options: fields.map((cd) => ({
        id: cd.value,
        label: cd.label ?? cd.defaultLabel,
        alwaysVisible: columnsAlwaysVisible,
      })),
    },
    stickyColumnsPreference: {
      firstColumns: {
        title: t('tables.stick_first_columns_title'),
        description: t('tables.stick_first_columns_description'),
        options: [
          { label: t('tables.stick_column_options.none'), value: 0 },
          { label: t('tables.stick_column_options.first'), value: 1 },
          { label: t('tables.stick_column_options.first_two'), value: 2 },
        ],
      },
      lastColumns: {
        title: t('tables.stick_last_columns_title'),
        description: t('tables.stick_last_columns_description'),
        options: [
          { label: t('tables.stick_column_options.none'), value: 0 },
          { label: t('tables.stick_column_options.last'), value: 1 },
        ],
      },
    },
    contentDensityPreference: t('tables.content_density'),
    wrapLinesPreference: t('tables.wrap_lines'),
    stripedRowsPreference: t('tables.striped_rows'),
  };

  return <CollectionPreferences {...collectionPreferencesProps} />;
};

export default CustomDatasourceCollectionPreferences;
